import { PUBLIC_GOOGLE_MAPS_API_KEY } from "$env/static/public";
import * as mapsLoader from "@googlemaps/js-api-loader";
import type { DeliveryReportData } from "$types/report";
import type { DeliveryReportRequest } from "src/routes/api/reports/+server";

let loader: mapsLoader.Loader;

export type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
export type AdvancedMarkerElementOptions =
  google.maps.marker.AdvancedMarkerElementOptions;
export type AutocompleteOptions = google.maps.places.AutocompleteOptions;
export type AutocompletePrediction = google.maps.places.AutocompletePrediction;
export type AutocompletionRequest = google.maps.places.AutocompletionRequest;
export type DirectionsRoute = google.maps.DirectionsRoute;
export type DrivingOptions = google.maps.DrivingOptions;
export type LatLngBounds = google.maps.LatLngBounds;
export type LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;
export type LatLngLiteral = google.maps.LatLngLiteral;
export type LatLng = google.maps.LatLng;
export type Map = google.maps.Map;
export type MapPadding = google.maps.Padding;
export type MapTypeStyle = google.maps.MapTypeStyle;
export type Marker = google.maps.Marker;
export type MarkerOptions = google.maps.MarkerOptions;
export type PlaceResult = google.maps.places.PlaceResult;
export type Polyline = google.maps.Polyline;
export type TrafficModel = "pessimistic" | "optimistic" | "bestguess";

export type MapMarker = {
  position: LatLngLiteral;
  icon:
    | string
    | (Omit<google.maps.Icon, "size" | "anchor"> & {
        size?: { width: number; height: number };
        rotate?: number;
      });
  anchor?: { x: number; y: number };
  elevation?: number;
  label?:
    | {
        heading: string;
        content: string;
        side?: "left" | "right";
      }
    | string;
  id?: string;
};

export type MapLine = {
  path: string | LatLngLiteral[];
  width?: number;
  color?: string;
};

export class PlaceAutocompleter {
  places: google.maps.PlacesLibrary;
  token: google.maps.places.AutocompleteSessionToken | undefined;
  autocompleteService: google.maps.places.AutocompleteService;
  placesService: google.maps.places.PlacesService;

  /**
   * Create a new PlaceAutocompleter
   */
  static async create() {
    const places = await importLibrary("places");
    return new PlaceAutocompleter(places);
  }

  private constructor(places: google.maps.PlacesLibrary) {
    this.places = places;
    this.autocompleteService = new this.places.AutocompleteService();

    const tmpElement = document.createElement("div");
    this.placesService = new this.places.PlacesService(tmpElement);

    // This token is only good for one session, which is one series of
    // findPlaces requests followed by one getPlaceLocation request.
    this.token = new this.places.AutocompleteSessionToken();
  }

  /**
   * Find places matching a query
   */
  async findPlaces(
    query: string,
    options?: Omit<AutocompletionRequest, "sessionToken" | "input">,
  ): Promise<AutocompletePrediction[]> {
    if (!this.token) {
      throw new Error("Session is no longer active");
    }

    const result = await this.autocompleteService.getPlacePredictions({
      ...options,
      input: query,
      sessionToken: this.token,
    });

    return result.predictions;
  }

  /**
   * Get the geographic location of a place.
   *
   * Calling this will end the PlaceAutocompleter's session.
   *
   * @param placeId - a Google Maps place ID
   * @returns the geolocation and address components of the place
   */
  async getPlaceLocation(placeId: string): Promise<{
    location: LatLngLiteral;
    address: NonNullable<PlaceResult["address_components"]>;
    formattedAddress: NonNullable<PlaceResult["formatted_address"]>;
    name: string;
  }> {
    const sessionToken = this.token;
    this.token = undefined;

    return new Promise<{
      location: LatLngLiteral;
      address: NonNullable<PlaceResult["address_components"]>;
      formattedAddress: NonNullable<PlaceResult["formatted_address"]>;
      name: string;
    }>((resolve, reject) =>
      this.placesService.getDetails(
        {
          placeId,
          sessionToken,
          fields: [
            "geometry",
            "address_components",
            "formatted_address",
            "name",
          ],
        },
        (place, status) => {
          if (
            status !== this.places.PlacesServiceStatus.OK ||
            !place ||
            !place.geometry?.location ||
            !place.address_components
          ) {
            reject(status);
          } else {
            resolve({
              location: {
                lat: place.geometry.location?.lat() ?? 0,
                lng: place.geometry.location?.lng() ?? 0,
              },
              address: place.address_components,
              formattedAddress: place.formatted_address ?? "",
              name: place.name ?? "",
            });
          }
        },
      ),
    );
  }
}

/**
 * Get the browser's current location
 */
export async function getCurrentLocation(): Promise<LatLngLiteral> {
  const loc = await new Promise<LatLngLiteral>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition((position) => {
      resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    }, reject);
  });
  return loc;
}

/**
 * Create a new map object hosted in a given container
 *
 * @param container - a DOM container for the map
 * @param options - map configuration options
 */
export async function createMap(
  container: HTMLElement,
  options: google.maps.MapOptions,
): Promise<Map> {
  const maps = await importLibrary("maps");
  return new maps.Map(container, options);
}

/**
 * Add a marker to a map
 *
 * @param map - the Map to add the marker to
 * @param position - a geolocation for the marker
 */
export async function addMarker(
  map: Map,
  position: LatLngLiteral,
  options: Omit<MapMarker, "position">,
): Promise<AdvancedMarkerElement> {
  const markerLib = await importLibrary("marker");
  const { icon, id, label, anchor, elevation } = options ?? {};

  const markerOpts: AdvancedMarkerElementOptions = {
    map,
    position,
  };

  let content: HTMLElement;
  const iconImage = document.createElement("img");

  if (typeof icon === "string") {
    iconImage.src = icon;
  } else {
    if (icon.size) {
      iconImage.style.width = `${icon.size.width}px`;
      iconImage.style.height = `${icon.size.height}px`;
    }

    if (icon.rotate) {
      iconImage.style.transform = `rotate(${icon.rotate}deg)`;
    }

    iconImage.src = icon.url;
  }

  content = iconImage;

  let labelSide: "right" | "left" | undefined;

  if (label) {
    content = document.createElement("div");
    content.classList.add(
      "flex",
      "flex-row",
      "items-center",
      "bg-charcoal/60",
      "p-1",
      "rounded-full",
      "text-white",
    );

    labelSide = typeof label === "string" ? "right" : label.side ?? "right";

    if (iconImage && labelSide === "right") {
      content.append(iconImage);
    }

    const lbl = document.createElement("div");
    lbl.classList.add("flex", "flex-col", "px-2");
    if (typeof label === "string") {
      lbl.textContent = label;
    } else {
      const lblHeading = document.createElement("div");
      lblHeading.textContent = label.heading;
      lblHeading.classList.add("font-bold", "text-[10px]");
      lbl.append(lblHeading);
      const lblContent = document.createElement("div");
      lblContent.textContent = label.content;
      lblContent.classList.add("text-[9px]");
      lbl.append(lblContent);
    }
    content.append(lbl);

    if (iconImage && labelSide === "left") {
      content.append(iconImage);
    }
  }

  if (anchor) {
    content.style.position = "relative";
    content.style.top = `${anchor.y}px`;
    content.style.left = `${anchor.x}px`;

    if (labelSide) {
      let iconWidth: number;
      if (icon && typeof icon === "object" && icon.size) {
        iconWidth = icon.size.width;
      } else {
        iconWidth = 0;
      }

      if (labelSide === "left") {
        content.style.transform = `translateX(calc(-50% + ${iconWidth / 2}px))`;
      } else {
        content.style.transform = `translateX(calc(50% - ${iconWidth / 2}px))`;
      }
    }
  }

  if (elevation && content) {
    let x = 0;
    let y = elevation;
    if (!label && icon && typeof icon !== "string" && icon.rotate) {
      const rads = icon.rotate * (Math.PI / 180);
      x = elevation * Math.sin(rads);
      y = elevation * Math.cos(rads);
    }
    content.style.filter = `drop-shadow(${x}px ${y}px 2px rgba(0, 0, 0, 0.5))`;
  }

  if (id && content) {
    content.setAttribute("data-marker-id", id);
  }

  markerOpts.content = content;

  return new markerLib.AdvancedMarkerElement(markerOpts);
}

type GoogleApi = {
  geometry: google.maps.GeometryLibrary;
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
  places: google.maps.PlacesLibrary;
  routes: google.maps.RoutesLibrary;
  core: google.maps.CoreLibrary;
};

/**
 * Return a maps API object
 */
async function importLibrary<K extends keyof GoogleApi>(
  lib: K,
): Promise<GoogleApi[K]> {
  if (!loader) {
    loader = new mapsLoader.Loader({
      apiKey: PUBLIC_GOOGLE_MAPS_API_KEY as string,
      version: "beta",
    });
  }
  return loader.importLibrary(lib) as Promise<GoogleApi[K]>;
}

/**
 * Get the location at by moving a given distance at a given heading.
 *
 * @param start - the starting location
 * @param heading - the heading in degrees clockwise from north
 * @param distance - the distance to move in meters
 */
export async function getLocationAtOffset(
  start: LatLngLiteral,
  heading: number,
  distance: number,
): Promise<LatLngLiteral> {
  const geometry = await importLibrary("geometry");
  const location = geometry.spherical.computeOffset(start, distance, heading);
  return { lat: location.lat(), lng: location.lng() };
}

export type SimplePlace = {
  location: LatLngLiteral;
  formattedAddress: string;
  name: string;
};

/**
 * Get a place near a given point
 *
 * @param location - the position to search around
 * @param radius - the radius to search in, in meters
 * @param maxCount - the maximum number of places to return
 */
async function findNearbyPlace(
  location: LatLngLiteral,
  options?: {
    radius?: number;
    maxCount?: number;
  },
): Promise<{ place: SimplePlace; distance: number } | undefined> {
  const { radius = 250, maxCount = 5 } = options ?? {};
  const geometry = await importLibrary("geometry");

  const resp = await fetch(
    `https://places.googleapis.com/v1/places:searchNearby`,
    {
      headers: {
        "Content-Type": "application/json",
        "X-Goog-FieldMask":
          "places.displayName,places.location,places.formattedAddress",
        "X-Goog-Api-Key": PUBLIC_GOOGLE_MAPS_API_KEY,
      },
      method: "POST",
      body: JSON.stringify({
        maxResultCount: maxCount,
        locationRestriction: {
          circle: {
            center: { latitude: location.lat, longitude: location.lng },
            radius,
          },
        },
      }),
    },
  );

  const results: {
    places: {
      location: { latitude: number; longitude: number };
      displayName: { text: string };
      formattedAddress: string;
    }[];
  } = await resp.json();

  let closestPlace: { distance: number; place: SimplePlace } | undefined =
    undefined;

  if (Array.isArray(results.places)) {
    for (const place of results.places) {
      const placeLoc = {
        lat: place.location.latitude,
        lng: place.location.longitude,
      };
      const distance = geometry.spherical.computeDistanceBetween(
        placeLoc,
        location,
      );
      if (!closestPlace || distance < closestPlace.distance) {
        closestPlace = {
          distance,
          place: {
            location: placeLoc,
            name: place.displayName.text,
            formattedAddress: place.formattedAddress,
          },
        };
      }
    }
  }

  return closestPlace;
}

const directions = [45, 225, 135, 315];

/**
 * Search for a nearby place.
 *
 * Check at various points around a given location for
 *
 * @param location - the position to search around
 * @param distance - the approximate distance the place should be from the
 * location, in meters
 */
export async function getNearbyPlace(
  location: LatLngLiteral,
  distance: number,
): Promise<{ place: SimplePlace; distance: number }> {
  // start searching a random direction
  const start = Math.floor(Math.random() * directions.length);
  for (let i = 0; i < directions.length; i++) {
    const dir = directions[(i + start) % directions.length];
    const point = await getLocationAtOffset(location, dir, distance);
    if (point) {
      const nearby = await findNearbyPlace(point);
      if (nearby) {
        return nearby;
      }
    }
  }

  throw new Error(`Unable to find a place near ${JSON.stringify(location)}`);
}

/**
 * Get a location that is halfway between two points
 */
export async function getMidPoint(
  a: LatLngLiteral,
  b: LatLngLiteral,
): Promise<LatLngLiteral> {
  const geometry = await importLibrary("geometry");
  const distance = geometry.spherical.computeDistanceBetween(a, b);
  const heading = geometry.spherical.computeHeading(a, b);
  const midpoint = geometry.spherical.computeOffset(a, distance / 2, heading);
  return midpoint.toJSON();
}

export async function getDistance(
  a: LatLngLiteral,
  b: LatLngLiteral,
): Promise<number> {
  const geometry = await importLibrary("geometry");
  return geometry.spherical.computeDistanceBetween(a, b);
}

/**
 * Get a route between two points
 *
 * @param from - the starting point
 * @param to - the ending point
 */
export async function getRoute(
  from: LatLngLiteral,
  to: LatLngLiteral,
  options?: Omit<DrivingOptions, "trafficModel"> & {
    trafficModel?: TrafficModel;
  },
): Promise<DirectionsRoute> {
  const routes = await importLibrary("routes");
  const service = new routes.DirectionsService();

  const drivingOptions = options
    ? {
        ...options,
        trafficModel:
          options?.trafficModel === "pessimistic"
            ? routes.TrafficModel.PESSIMISTIC
            : options?.trafficModel === "optimistic"
              ? routes.TrafficModel.OPTIMISTIC
              : undefined,
      }
    : undefined;

  const result = await service.route({
    origin: from,
    destination: to,
    travelMode: routes.TravelMode.DRIVING,
    drivingOptions,
  });

  if (!result.routes[0]) {
    throw new Error(
      `No route from ${JSON.stringify(from)} to ${JSON.stringify(to)}`,
    );
  }

  return result.routes[0];
}

/**
 * Add a route polyline to a map
 *
 * @param map - the map to add the polyline to
 * @param path - an encoded polyline path
 */
export async function addPolyline(
  map: Map,
  path: LatLngLiteral[],
  options?: Pick<google.maps.PolylineOptions, "strokeColor" | "strokeWeight">,
): Promise<Polyline> {
  const maps = await importLibrary("maps");
  return new maps.Polyline({
    ...options,
    map,
    path,
  });
}

/**
 * Decode an encoded polyline into points
 *
 * @param path - an encoded polyline
 */
export async function decodePolyline(path: string): Promise<LatLngLiteral[]> {
  const geometry = await importLibrary("geometry");
  return geometry.encoding.decodePath(path).map((p) => p.toJSON());
}

/**
 * Create a polyline arc
 *
 * @param a - the start location
 * @param b - the end location
 * @param radius - the radius of the circle containing the arc in meters
 */
export async function createArc(
  a: LatLngLiteral,
  b: LatLngLiteral,
  radius?: number,
): Promise<LatLngLiteral[]> {
  const geometry = await importLibrary("geometry");
  const points: LatLngLiteral[] = [];
  const start = b.lng < a.lng ? b : a;
  const end = start === b ? a : b;

  // The straight-line distance between the two points on the arc
  const dist = geometry.spherical.computeDistanceBetween(a, b);
  radius = radius ?? dist;

  // The distance from the midpoint of the line between the two points to the
  // center of the arc (sagitta)
  const sag = radius - Math.sqrt(radius ** 2 - (dist / 2) ** 2);

  // The midpoint between the two points
  const midPoint = await getMidPoint(a, b);

  // The sag heading is a right angle to the heading between the two points
  const sagAngle = 90;
  const sagHeading = geometry.spherical.computeHeading(start, end) + sagAngle;

  // The center of the circle containing the arc
  const center = geometry.spherical
    .computeOffset(midPoint, radius - sag, sagHeading)
    .toJSON();

  // The headings from the center point towards the start and end points
  const startHeading = geometry.spherical.computeHeading(center, start);
  const endHeading = geometry.spherical.computeHeading(center, end);

  points.push(start);

  let i = 0;
  while (startHeading + i <= endHeading || endHeading + i <= startHeading) {
    if (radius < 0) {
      points.push(
        geometry.spherical
          .computeOffset(center, radius, startHeading - i)
          .toJSON(),
      );
    } else {
      points.push(
        geometry.spherical
          .computeOffset(center, radius, startHeading + i)
          .toJSON(),
      );
    }
    i += 0.25;
  }

  return start == a ? points : points.reverse();
}

/**
 * Return the total distance of a route in meters.
 */
export function getRouteDistance(route: DirectionsRoute): number {
  return route.legs.reduce(
    (total, leg) => total + (leg.distance?.value ?? 0),
    0,
  );
}

/**
 * Return the total length of a path in meters.
 */
export async function getPathDistance(
  path: LatLngLiteral[] | LatLng[],
): Promise<number> {
  const geometry = await importLibrary("geometry");
  let distance = 0;

  for (let i = 1; i < path.length - 1; i++) {
    distance += geometry.spherical.computeDistanceBetween(path[i - 1], path[i]);
  }

  return distance;
}

/**
 * Return location for a marker along a path
 *
 * @param {LatLngLiteral[]} path - the path to place the marker on
 * @param {number} progress - how far along the path to place the marker, in [0, 1]
 */
export async function getMarkerLocationAndHeading(
  path: LatLngLiteral[] | LatLng[],
  progress: number,
): Promise<{
  location: LatLngLiteral;
  heading: number;
}> {
  const geometry = await importLibrary("geometry");
  const pathLength = await getPathDistance(path);
  const targetDistance = pathLength * progress;
  let distanceTraveled = 0;

  let index = 0;
  for (index = 1; index < path.length - 1; index++) {
    distanceTraveled += geometry.spherical.computeDistanceBetween(
      path[index - 1],
      path[index],
    );
    if (distanceTraveled >= targetDistance) {
      break;
    }
  }

  const heading = geometry.spherical.computeHeading(
    path[index],
    path[index + 1],
  );

  const loc = path[index];
  const location: LatLngLiteral = isLatLngLiteral(loc) ? loc : loc.toJSON();

  return {
    location,
    heading,
  };
}

export async function getOrientation(
  a: LatLngLiteral,
  b: LatLngLiteral,
): Promise<"vertical" | "horizontal"> {
  const geometry = await importLibrary("geometry");
  const heading = geometry.spherical.computeHeading(a, b);
  if (heading <= 45 || (heading > 135 && heading <= 225) || heading > 315) {
    return "vertical";
  }
  return "horizontal";
}

/**
 * Return the drive time of a route in seconds.
 */
export function getRouteTime(route: DirectionsRoute): number {
  return route.legs.reduce(
    (total, leg) =>
      total + (leg.duration_in_traffic?.value ?? leg.duration?.value ?? 0),
    0,
  );
}

/**
 * Convert a given value in meters to miles.
 */
const METER_MILE_CONVERSION = 1609.344;

export function metersToMiles(value: number): number {
  return value / METER_MILE_CONVERSION;
}

export function milesToMeters(value: number): number {
  return value * METER_MILE_CONVERSION;
}

/**
 * Change a map's zoom to fit a set of markers.
 */
export async function fitBounds(
  map: Map,
  points: LatLngLiteral[],
  options?: { padding?: MapPadding },
): Promise<void> {
  const core = await importLibrary("core");

  const bounds = new core.LatLngBounds();
  for (const point of points) {
    bounds.extend(point);
  }

  map.fitBounds(bounds, options?.padding);
}

const dayNumbers = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const;

/**
 * Return a date that is the next occurrence of a given weekday, optionally at a given time.
 */
export function getDayOfWeek(
  day: keyof typeof dayNumbers,
  hours?: number,
  minutes?: number,
  seconds?: number,
): Date {
  const nextDay = new Date();
  const dayNum = dayNumbers[day];
  const currDay = nextDay.getDay();
  if (currDay !== dayNum) {
    nextDay.setDate(nextDay.getDate() + ((dayNum + 7 - currDay) % 7));
  } else if (currDay === dayNum) {
    nextDay.setDate(nextDay.getDate() + 7);
  }

  if (hours !== undefined) {
    nextDay.setHours(hours);
    if (minutes !== undefined) {
      nextDay.setMinutes(minutes);
      if (seconds !== undefined) {
        nextDay.setSeconds(seconds);
      }
    }
  }

  return nextDay;
}

export function isLatLngLiteral(value: unknown): value is LatLngLiteral {
  return (
    typeof value === "object" &&
    value !== null &&
    "lat" in value &&
    typeof value.lat === "number" &&
    "lng" in value &&
    typeof value.lng === "number"
  );
}

export async function getEastWestDirection(
  path: string | LatLngLiteral[] | LatLng[],
): Promise<"east" | "west"> {
  let route: LatLngLiteral[] | LatLng[];

  if (typeof path === "string") {
    route = await decodePolyline(path);
  } else {
    route = path;
  }

  const a = route[0];
  const b = route[route.length - 1];

  const first: LatLngLiteral = isLatLngLiteral(a) ? a : a.toJSON();
  const last: LatLngLiteral = isLatLngLiteral(b) ? b : b.toJSON();

  return first.lng < last.lng ? "east" : "west";
}

export type PositionRelation = {
  left: boolean;
  above: boolean;
  below: boolean;
  right: boolean;
  beside: boolean;
};

/**
 * Get the position of a relative to b (above, below, left, right)
 */
export function getRelativePosition(
  a: HTMLElement,
  b: HTMLElement,
): PositionRelation {
  const aRect = a.getBoundingClientRect();
  const bRect = b.getBoundingClientRect();
  return {
    left: aRect.x < bRect.x,
    above: aRect.y < bRect.y,
    below: aRect.y > bRect.y,
    right: aRect.x > bRect.x,
    beside: aRect.y <= bRect.y + bRect.height,
  };
}

async function stopAndThinkSince(start: number) {
  /** Minimum time after submitting to wait before loading the report */
  const thinkingTime = 5000;
  const elapsed = Date.now() - start;
  return new Promise((resolve) => {
    setTimeout(resolve, Math.max(thinkingTime - elapsed, 0));
  });
}

async function logReportRequest(logData: DeliveryReportRequest) {
  return fetch("/api/reports", {
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(logData),
  });
}

type DeliveryReportSpecification = {
  company: string;
  distance: number;
  reportLink: string;
  placeId: string;
  placeFinder: PlaceAutocompleter;
  immediate?: boolean;
};

export async function runDeliveryReport(
  data: DeliveryReportSpecification,
): Promise<URL> {
  const {
    company,
    distance,
    reportLink,
    placeId,
    placeFinder,
    immediate = false,
  } = data;
  const start = Date.now();

  try {
    const location = await placeFinder.getPlaceLocation(placeId);

    const locationName =
      location.address.find((ac) => ac.types.includes("route"))?.short_name ??
      location.name ??
      "";

    const streetNumber = location.address.find((ac) =>
      ac.types.includes("street_number"),
    )?.short_name;
    const shortAddress = streetNumber
      ? `${streetNumber} ${locationName}`
      : locationName;

    const nearby = await getNearbyPlace(
      location.location,
      milesToMeters(Number(distance ?? 5)),
    );

    // Get next Monday at 4pm, which should be a fairly high traffic time
    const nextMonday = getDayOfWeek("Monday", 16, 0, 0);

    const route = await getRoute(location.location, nearby.place.location, {
      departureTime: nextMonday,
      trafficModel: "pessimistic",
    });

    const droneDistance = await getDistance(
      location.location,
      nearby.place.location,
    );
    const midPoint = await getMidPoint(
      location.location,
      nearby.place.location,
    );

    const reportData: DeliveryReportData = {
      home: location.location,
      homeId: placeId,
      homeAddress: location.formattedAddress,
      homeShortAddress: shortAddress,
      locationName,
      nearby: nearby.place,
      route: {
        path: route.overview_polyline,
        distance: getRouteDistance(route),
        time: getRouteTime(route),
      },
      droneDistance,
      midPoint,
    };

    const query = new URLSearchParams({
      company,
      report: window.btoa(JSON.stringify(reportData)),
    });

    const reportUrl = new URL(`${reportLink}?${query}`, window.location.href);

    if (!immediate) {
      await stopAndThinkSince(start);
    }

    return reportUrl;
  } catch (error) {
    if (!immediate) {
      await stopAndThinkSince(start);
    }
    throw error;
  }
}

export type HubspotDeliveryReportContext = {
  pageName?: string;
  pageUri?: Location | URL;
};

export type HubspotDeliveryReportRequest = {
  formId: string;
  firstName: string;
  lastName: string;
  company: string;
  address: string;
  email: string;
  region: string;
  reportUrl?: URL;
  context?: HubspotDeliveryReportContext;
};

export async function saveDeliveryReport(data: HubspotDeliveryReportRequest) {
  return fetch("/api/reports/deliver", {
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function enableAutocomplete(
  element: HTMLInputElement | null,
  options?: AutocompleteOptions,
) {
  if (!element) return;
  const places = await importLibrary("places");
  return new places.Autocomplete(element, options);
}
